import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import moment from 'moment';
import momentPropTypes from 'react-moment-proptypes';
import Toggle from 'react-toggle';
import isEqual from 'lodash-es/isEqual';
import intlShape from '@/shared/utils/intlShape';
import Axios from 'axios';

import RelationSearch from '@/shared/components/input/RelationSearch';
import PeriodButtonGroup, {
  getPeriodStartEndDates,
  pastToPrevMonth,
} from '@/shared/components/PeriodButtonGroup';
import ResourceTypeButtonGroup from '@/shared/components/ResourceTypeButtonGroup';
import UserSearch from '@/shared/components/input/UserSearch';
import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import userStorage from '@/shared/services/userStorage';

import DynamicFilter, {
  getDynamicFiltersFromDefaultFilters,
  handleDynamicFiltersChanged,
  initDynamicFilters,
  resetDynamicFilters,
} from '@/shared/components/DynamicFilter';
import SavedFiltersButton from '@/shared/components/SavedFiltersButton';
import { CanRead } from '@/shared/components/security';
import GroupByFilter from './GroupByFilter';
import config from '../../../config';
import api from '../../../shared/api';
import messages from '../reportingMessages';
import ResourceMultiSelect from '@/shared/components/input/multiselect/ResourceMultiSelect';
import RelationTypeButtonGroup from '@/shared/components/RelationTypeButtonGroup';
import CrmTypeButtonGroup from '@/shared/components/CrmTypeButtonGroup';

export const defaultFilters = {
  relationId: null,
  relationName: null,
  dateFrom: moment.utc().startOf('year'),
  dateUntil: moment.utc().endOf('year'),
  period: 'year',
  relationTypes: [],
  crmTypes: [],
  ownCompanies: [],
  legalEntities: [],
  resourceTypes: [0],
  mainTaskTemplateId: null,
  userId: null,
  groupBy: [],
  withLabels: [],
  withoutLabels: [],
  withEmployeeLabels: [],
  assistant: null, // UserId
  responsible: null, // UserId
  associate: null, // UserId
  assistantBackup: null, // UserId
  billingUser: null, // UserId
  seniorUser: null, // UserId
  teamCodes: [],
  referenceYear: null,
  assignmentTemplateId: null,
  employeeType: null,
  resources: [],
  relationGroups: [],
  sectors: [],
};
export const getParamsFromFilters = filters => {
  const {
    relationId,
    dateFrom: from,
    dateUntil: until,
    relationTypes,
    crmTypes,
    ownCompanies,
    legalEntities,
    resourceTypes,
    userId,
    groupBy,
    mainTaskTemplateId,
    withLabels,
    withoutLabels,
    withEmployeeLabels,
    assistant,
    responsible,
    associate,
    assistantBackup,
    billingUser,
    seniorUser,
    teamCodes,
    referenceYear,
    assignmentTemplateId,
    employeeType,
    resources,
    relationGroups,
    sectors,
  } = filters;

  return {
    relationId,
    from,
    until,
    relationTypes,
    crmTypes,
    resourceTypes,
    ownCompanies,
    legalEntities,
    userId,
    groupBy,
    mainTaskTemplateId,
    withLabels,
    withoutLabels,
    withEmployeeLabels,
    assistant,
    responsible,
    associate,
    assistantBackup,
    billingUser,
    seniorUser,
    teamCodes,
    referenceYear,
    assignmentTemplateId,
    employeeType,
    resources,
    relationGroups,
    sectors,
  };
};
const allDynamicFilters = [
  ...getDynamicFiltersFromDefaultFilters(defaultFilters, 'relationTypes', 'crmTypes'),
  { id: 'mainTaskTemplateId', label: messages.TaskTemplate },
  { id: 'resources', label: messages.WithPriceListItem },
];
const getIgnoredFilters = filters => {
  if (filters.period) {
    // Ignore date filters if there is a period
    return ['dateFrom', 'dateUntil'];
  }
  return [];
};

class RegistrationsPerReportFilters extends Component {
  state = {
    labels: [],
    dynamicFilters: [],
  };

  constructor(props) {
    super(props);
    this.dynamicFiltersStorageKey = `${props.reportKey}DynamicFilters`;
  }

  componentDidMount() {
    const { intl, filters } = this.props;
    this.labelCancelToken = Axios.CancelToken.source();
    this.employeeLabelCancelToken = Axios.CancelToken.source();
    const labelEndpoint = `${config.appUrls.api}/api/resource/tags`;
    const employeeLabelEndpoint = `${config.appUrls.api}/api/employee/tags`;

    Promise.all([
      api.base.get(labelEndpoint, {
        cancelToken: this.labelCancelToken.token,
      }),
      api.base.get(employeeLabelEndpoint, {
        cancelToken: this.employeeLabelCancelToken.token,
      }),
    ]).then(([{ data: labels }, { data: employeeLabels }]) => {
      this.setState({
        labels: labels
          .map(x => ({ id: x.id, label: x.name, sort: x.name.toLowerCase(), color: x.color }))
          .sort(dynamicSort('sort')),
        employeeLabels: employeeLabels
          .map(x => ({ id: x.id, label: x.name, sort: x.name.toLowerCase() }))
          .sort(dynamicSort('sort')),
        dynamicFilters: initDynamicFilters(
          intl,
          allDynamicFilters,
          userStorage.getItem(this.dynamicFiltersStorageKey) || [],
          filters,
          defaultFilters,
        ),
      });
    });
  }

  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };

    if (JSON.stringify(filters) !== JSON.stringify(newFilters)) onFilterChange(newFilters);
  };
  dynamicFilterChanged = (item, selected) => {
    this.setState(({ dynamicFilters }) => {
      const { filters } = this.props;
      const newFilters = handleDynamicFiltersChanged(
        dynamicFilters,
        item,
        selected,
        filters,
        defaultFilters,
      );
      userStorage.setItem(
        this.dynamicFiltersStorageKey,
        newFilters.filter(x => x.selected).map(x => x.id),
      );
      if (
        !selected &&
        item.id !== 'selectAll' &&
        !isEqual(filters[item.id], defaultFilters[item.id])
      ) {
        // Reset filter when it's removed
        this.filterChanged({ [item.id]: defaultFilters[item.id] });
      }
      return { dynamicFilters: newFilters };
    });
  };
  savedFilterChanged = filters => {
    const { onFilterChange } = this.props;
    const newFilters = { ...defaultFilters, ...filters };
    if (typeof newFilters.dateFrom === 'string') newFilters.dateFrom = moment.utc(filters.dateFrom);
    if (typeof newFilters.dateUntil === 'string')
      newFilters.dateUntil = moment.utc(filters.dateUntil);

    if (newFilters.period) {
      const [dateFrom, dateUntil] = getPeriodStartEndDates(newFilters.period);
      newFilters.dateFrom = dateFrom;
      newFilters.dateUntil = dateUntil;
    }

    onFilterChange(newFilters);

    this.setState(({ dynamicFilters }) => ({
      dynamicFilters: resetDynamicFilters(dynamicFilters, newFilters, defaultFilters),
    }));
  };
  dateChanged = (dateFrom, dateUntil) => {
    this.filterChanged({ dateFrom, dateUntil, period: null });
  };
  relationChanged = item => {
    if (!item || !item.id) {
      this.filterChanged({ relationId: null, relationName: null });
    } else {
      this.filterChanged({
        relationId: item.key,
        relationName: item.label,
      });
    }
  };
  showDetailsChanged = showDetail => {
    const { filters } = this.props;
    const groupsWithoutDetails = (filters.groupBy || []).filter(x => x !== 99);

    this.filterChanged({
      groupBy: showDetail ? [...groupsWithoutDetails, 99] : groupsWithoutDetails,
    });
  };
  resetFilters = () => {
    this.filterChanged({ ...defaultFilters });
  };

  renderDynamicFilter = ({ type, filters, intl }) => {
    switch (type) {
      case 'mainTaskTemplateId': {
        const { mainTaskTemplates } = this.props;
        return (
          <div className="col-lg-4 col-md-6 margin5B">
            <select
              value={filters.mainTaskTemplateId || ''}
              onChange={e => this.filterChanged({ mainTaskTemplateId: e.target.value })}
              className="form-control"
            >
              <option value="">{intl.formatMessage(messages.AllTaskTemplates)}</option>
              <option value="00000000-0000-0000-0000-000000000000">
                {intl.formatMessage(messages.WithoutTemplate)}
              </option>
              {mainTaskTemplates &&
                mainTaskTemplates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.label}
                  </option>
                ))}
            </select>
          </div>
        );
      }
      case 'resources':
        return (
          <div className="col-lg-4 col-md-6 margin5B">
            <ResourceMultiSelect
              resourceTypes={filters.resourceTypes}
              selection={filters.resources}
              onChange={resources => this.filterChanged({ resources })}
              placeholder={intl.formatMessage(messages.WithPriceListItem)}
            />
          </div>
        );
      default: {
        console.warn(`Missing filter: ${type}`);
        return null;
      }
    }
  };

  render() {
    const { filters, intl, rootGroup, reportKey } = this.props;
    const { labels, employeeLabels, dynamicFilters } = this.state;
    const initRelation = filters.relationId
      ? {
          key: filters.relationId,
          label: filters.relationName,
          invoiceStreamId: filters.invoiceStreamId,
        }
      : null;

    const showDetails = (filters.groupBy || []).some(x => x === 99);
    const groupsWithoutDetails = (filters.groupBy || []).filter(x => x !== 99);
    const visibleFilters = dynamicFilters.filter(x => x.selected).map(x => x.id);

    return (
      <div>
        <div className="row">
          <div className="col-lg-4">
            <div className="form-group">
              <RelationSearch onChange={this.relationChanged} initialSelectedItem={initRelation} />
            </div>
          </div>
          <div className="col-lg-4">
            <div className="form-group">
              <RelationTypeButtonGroup
                selectedTypes={filters.relationTypes}
                onChange={value => {
                  this.filterChanged({ relationTypes: value });
                }}
              />
            </div>
          </div>
          <div className="col-lg-4">
            <div className="form-group">
              <CrmTypeButtonGroup
                selectedTypes={filters.crmTypes}
                onChange={value => {
                  this.filterChanged({ crmTypes: value });
                }}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-3 col-md-4">
            <div className="form-group">
              <DelayedDateRangePicker
                startDate={filters.dateFrom}
                startDateId="RegistrationsPerFiltersDateFrom"
                endDate={filters.dateUntil}
                endDateId="RegistrationsPerFiltersDateUntil"
                onDatesChange={({ startDate, endDate }) => this.dateChanged(startDate, endDate)}
                startDatePlaceholderText={intl.formatMessage(messages.RegistrationDateFrom)}
                endDatePlaceholderText={intl.formatMessage(messages.RegistrationDateUntil)}
                block
              />
            </div>
          </div>
          <div className="col-lg-9 col-md-8">
            <div className="form-group">
              <PeriodButtonGroup
                dateFrom={filters.dateFrom}
                dateUntil={filters.dateUntil}
                period={filters.period}
                onChange={this.filterChanged}
                visiblePeriods={pastToPrevMonth}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-8 col-md-6">
            <div className="form-group">
              <ResourceTypeButtonGroup
                selectedTypes={filters.resourceTypes}
                onChange={value => {
                  this.filterChanged({ resourceTypes: value });
                }}
              />
            </div>
          </div>
          <CanRead on="RegistrationOthers">
            <div className="col-lg-4 col-md-6">
              <div className="form-group">
                <UserSearch
                  onChange={user => this.filterChanged({ userId: user ? user.id : null })}
                  value={filters.userId}
                  placeholder={intl.formatMessage(messages.FilterRegistrationUser)}
                  withSystemUser
                />
              </div>
            </div>
          </CanRead>
        </div>
        {visibleFilters && visibleFilters.length > 0 ? (
          <div className="row hidden-xs hidden-sm">
            {visibleFilters.map(id => (
              <DynamicFilter
                key={id}
                type={id}
                intl={intl}
                labels={labels}
                employeeLabels={employeeLabels}
                filters={filters}
                filterChanged={this.filterChanged}
                renderCustomFilter={this.renderDynamicFilter}
                relationUserSearchParams={{ withUnassigned: false }}
                labelsIdProp="id"
              />
            ))}
          </div>
        ) : null}
        <div className="row">
          <div className="col-lg-8 col-md-6">
            <div className="form-group">
              <GroupByFilter
                value={groupsWithoutDetails}
                onChange={grps =>
                  this.filterChanged({ groupBy: [...(grps || []), ...(showDetails ? [99] : [])] })
                }
                hidden={[rootGroup, 99]}
              />
            </div>
            <div className="form-group">
              <SavedFiltersButton
                screen={reportKey}
                currentFilters={filters}
                defaultFilters={defaultFilters}
                onFiltersChanged={this.savedFilterChanged}
                ignoreFilters={getIgnoredFilters(filters)}
                dynamicFilters={dynamicFilters}
                onDynamicFiltersChanged={this.dynamicFilterChanged}
              />
            </div>
          </div>
          <div className="col-lg-4 col-md-6">
            <div className="form-group">
              <label className="react-toggle-wrapper margin10T">
                <Toggle
                  checked={showDetails}
                  onChange={e => this.showDetailsChanged(e.target.checked)}
                />
                <FormattedMessage className="label-text" {...messages.ShowDetails} />
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

RegistrationsPerReportFilters.propTypes = {
  intl: intlShape.isRequired,
  filters: PropTypes.shape({
    relationId: PropTypes.string,
    dateFrom: momentPropTypes.momentObj,
    dateUntil: momentPropTypes.momentObj,
    period: PropTypes.oneOf(pastToPrevMonth),
    relationTypes: PropTypes.arrayOf(PropTypes.number),
    ownCompanies: PropTypes.arrayOf(PropTypes.string),
    resourceTypes: PropTypes.arrayOf(PropTypes.number),
    crmTypes: PropTypes.arrayOf(PropTypes.number),
    groupBy: PropTypes.arrayOf(PropTypes.number),
    mainTaskTemplateId: PropTypes.string,
    responsible: PropTypes.string,
    assistant: PropTypes.string,
    associate: PropTypes.string,
    assistantBackup: PropTypes.string,
    billingUser: PropTypes.string,
    seniorUser: PropTypes.string,
    userId: PropTypes.string,
  }),
  onFilterChange: PropTypes.func.isRequired,
  rootGroup: PropTypes.number.isRequired,
  reportKey: PropTypes.string.isRequired,
};
RegistrationsPerReportFilters.defaultProps = {
  filters: { ...defaultFilters },
};
export default injectIntl(RegistrationsPerReportFilters);
